import React, { useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { SignalCommandCenter } from './components/SignalCommandCenter';
import { AlertsPanel } from './components/AlertsPanel';
import { TabbedActivityPanel } from './components/TabbedActivityPanel';
import { SessionStats } from './components/SessionStats';
import { OptionsPanel } from './components/OptionsPanel';
import { HFTSignalPanel, TapeLog, SimulationResultsPanel } from './components/HFTPanel';
import { TridentPanel } from './components/SuperModelPanel';
import { GodScoreCommandCenter } from './components/GodScoreCommandCenter';
import { KeyLevels } from './components/KeyLevels';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { DataHealthAlert } from './components/DataHealthAlert';
import DhanHealthBanner from './components/admin/DhanHealthBanner';
import { SymbolSelector, EngineStatusBar } from './components/SymbolSelector';
import { AdminDashboard } from './components/AdminDashboard';
import UserDashboard from './components/UserDashboard';   // 147j+16bo Ship 2
import { AuthPage, LoginPage as AuthLoginPage } from './components/AuthPage';
import { AuthCallbackPage } from './components/AuthCallbackPage';
import { HoldingPage } from './components/HoldingPage';
import { AuthProvider, useAuth, ProtectedRoute } from './contexts/AuthContext';
import { MarketProvider, useMarket } from './contexts/MarketProvider';
import { Toaster } from './components/ui/sonner';
import TimeMachine from './components/TimeMachine';  // SESSION 75: Signal Replay Engine
import AdvancedSignalsPanel from './components/AdvancedSignalsPanel';  // SESSION 95: Phase 1 High-Accuracy Strategies
import InvitePage from './components/InvitePage';  // SESSION 136: Dedicated invite landing page

// Production-ready API base URL
// Uses REACT_APP_BACKEND_URL if set, otherwise defaults to relative path for same-origin deployment
const API = process.env.REACT_APP_BACKEND_URL || '';

// Log environment for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('🌐 API Base URL:', API || '(relative - same origin)');
}

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('sidebar_collapsed');
      setSidebarCollapsed(saved === 'true');
    };
    
    // Poll for localStorage changes (since storage event only fires across tabs)
    const interval = setInterval(handleStorageChange, 100);
    return () => clearInterval(interval);
  }, []);

  // Session 147j+16bq — In focused mode, the watchlist rail (NIFTY /
  // BANKNIFTY / S&R / VIX / Flow) has no live ticks to display because
  // TrueData is off. Hide it entirely so the admin sidebar (NAVIGATION +
  // ADMINISTRATION + RESEARCH) is the leftmost element. The watchlist
  // is restored when REACT_APP_FOCUS_MODE=false.
  const focusMode = (process.env.REACT_APP_FOCUS_MODE || 'true').toLowerCase() === 'true';

  return (
    <div className="min-h-screen w-screen flex overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
      {!focusMode && <Sidebar defaultCollapsed={sidebarCollapsed} />}
      {/* MAIN CONTENT: Flex-grow to fill all remaining space */}
      <div 
        className="flex-1 min-w-0 overflow-hidden"
        style={{ marginLeft: 0, paddingLeft: 0 }}
        data-testid="main-content-area"
      >
        {children}
      </div>
    </div>
  );
};

// Timeframe options
const TIMEFRAMES = [
  { key: "1s", label: "1s" },
  { key: "1m", label: "1m" },
  { key: "5m", label: "5m" },
  { key: "10m", label: "10m" },
  { key: "15m", label: "15m" },
  { key: "1h", label: "1H" },
  { key: "2h", label: "2H" },
  { key: "1D", label: "1D" },
];

// === PRICE FREEZE: Helper functions for price caching ===
const getPriceCacheKey = (symbol) => `price_cache_${symbol}`;
const getCachedPrice = (symbol) => {
  try {
    return parseFloat(sessionStorage.getItem(getPriceCacheKey(symbol))) || 0;
  } catch {
    return 0;
  }
};
const setCachedPrice = (symbol, price) => {
  try {
    sessionStorage.setItem(getPriceCacheKey(symbol), price.toString());
  } catch {
    // Ignore storage errors
  }
};

// Trading Dashboard Component - Now uses MarketProvider context
const TradingDashboard = ({ initialSymbol = "NIFTY" }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // === USE GLOBAL MARKET CONTEXT (no local WebSocket!) ===
  const { 
    store, 
    activeSymbol, 
    setActiveSymbol, 
    connectionStatus,
    activeData,  // SESSION 66: Direct data reference for re-renders
    niftyData: ctxNiftyData,  // SESSION 66: Direct data references
    bankniftyData: ctxBankniftyData,
    getActiveData,
    getSymbolData,
  } = useMarket();
  
  // Local UI state
  const [isChangingSymbol, setIsChangingSymbol] = useState(false);
  
  // Get data for active symbol from global store
  // SESSION 66 FIX: Use activeData directly instead of getActiveData()
  // This ensures component re-renders when store updates
  const symbolData = activeData;
  
  // Get data for BOTH symbols (for NASA Control Room dual-view)
  const niftyData = ctxNiftyData;
  const bankniftyData = ctxBankniftyData;
  
  // Extract all data from store (replaces useMarketFeed destructuring)
  const {
    candles = [],
    hftCandles = [],
    deltaData = [],
    cumulativeDeltaData = [],
    vwapData = [],
    alerts = [],
    sessionStats = { cumulative_delta: 0, session_volume: 0, vwap: 0, tick_count: 0 },
    latestCandle = null,
    ltp = null,
    hftSignal = null,
    pocLineData = [],
    tape = [],
    tradingStats = null,
    tradeEvents = [],
    gexLevels = null,
    tridentStatus = null,
    marketProfile = null,
    godScore = null,
    dpocLineData = [],
    strategyPulse = null,
    futuresPrice = 0,
    spotPrice = 0,
  } = symbolData;
  
  // Additional state defaults (not in store yet)
  const sessionDate = null;
  const historicalData = null;
  const superSignal = null;
  
  // Local UI state
  const [selectedTimeframe, setSelectedTimeframe] = useState("1s");
  const [timeframeCandles, setTimeframeCandles] = useState([]);
  const [timeframeDelta, setTimeframeDelta] = useState([]);
  const [timeframeCvd, setTimeframeCvd] = useState([]);
  const [timeframeVwap, setTimeframeVwap] = useState([]);

  // SESSION 70b: REMOVED route sync effect
  // The sidebar toggle now controls activeSymbol directly via context
  // We no longer sync initialSymbol to activeSymbol to allow instant toggles
  // Initial symbol is only set ONCE when component first mounts
  useEffect(() => {
    // Only set initial symbol on FIRST mount (when no symbol is set yet)
    // This preserves the sidebar toggle's ability to change symbols
    if (activeSymbol === 'NIFTY' && initialSymbol !== 'NIFTY') {
      setActiveSymbol(initialSymbol);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency - run only once on mount

  // Connection status from context (NEVER flickers!)
  const isConnected = connectionStatus.isConnected;
  const connectionState = {
    status: connectionStatus.status,
    data_source: connectionStatus.dataSource,
    is_connected: connectionStatus.isConnected,
    is_market_open: connectionStatus.isMarketOpen,
  };
  
  // Market status (derived from connection)
  const marketStatus = connectionStatus.isMarketOpen ? 'open' : 'closed';

  // Determine if we're viewing historical data
  const dataMode = connectionStatus.dataSource === 'historical' ? 'historical' : 'live';
  const isStreaming = connectionStatus.isConnected;
  const isHistoricalMode = dataMode === 'historical' || !isStreaming;
  
  // Determine if disconnected - BUT NOT in replay mode
  const isReplayMode = connectionStatus.status === 'replay' || connectionStatus.status === 'replay_waiting';
  const isDisconnected = connectionStatus.status === 'disconnected' && !connectionStatus.isConnected && !isReplayMode;
  
  // Current price from store
  const rawPrice = (typeof ltp === 'object' ? ltp?.price : ltp) || latestCandle?.close || futuresPrice || 0;
  
  // Update price cache with valid price
  useEffect(() => {
    if (rawPrice > 0) {
      setCachedPrice(activeSymbol, rawPrice);
    }
  }, [rawPrice, activeSymbol]);
  
  // Use cached price if current is 0
  const currentPrice = rawPrice > 0 ? rawPrice : getCachedPrice(activeSymbol);

  // Handle symbol change - just updates context, no WebSocket recreation!
  const handleSymbolChange = useCallback(async (newSymbol) => {
    if (newSymbol === activeSymbol) return;
    setActiveSymbol(newSymbol);
    
    // Clear timeframe data immediately
    setTimeframeCandles([]);
    setTimeframeDelta([]);
    setTimeframeCvd([]);
    setTimeframeVwap([]);
    
    try {
      // Notify backend of view change (non-blocking)
      fetch(`${API}/api/symbol/switch/${newSymbol}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      }).catch(err => console.warn('Symbol switch notification failed:', err));
      
      console.log(`🔄 Switching to ${newSymbol}`);
    } catch (err) {
      console.error('Failed to change symbol:', err);
    }
  }, [activeSymbol, setActiveSymbol]);

  // Reset session function (calls backend API)
  const resetSession = useCallback(async () => {
    try {
      await fetch(`${API}/api/hft/reset-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      console.log('Session reset');
    } catch (err) {
      console.error('Failed to reset session:', err);
    }
  }, []);

  // Check data source status and schedule/zombie killer status
  const [isLive, setIsLive] = useState(false);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [zombieKillerInfo, setZombieKillerInfo] = useState(null);
  
  useEffect(() => {
    const checkDataSource = async () => {
      try {
        const res = await fetch(`${API}/api/data-source`);
        const data = await res.json();
        setIsLive(data.is_live);
      } catch (err) {
        console.error('Failed to check data source:', err);
      }
    };
    
    const checkScheduleAndFeed = async () => {
      try {
        // Fetch schedule status
        const scheduleRes = await fetch(`${API}/api/schedule/status`);
        if (scheduleRes.ok) {
          const scheduleData = await scheduleRes.json();
          setScheduleInfo(scheduleData);
        }
        
        // Fetch feed status for zombie killer info
        // SESSION 86k: Only update zombie info if retry count is actually non-zero
        // and connection is NOT currently established (prevents banner re-appearing)
        const feedRes = await fetch(`${API}/api/feed/status`);
        if (feedRes.ok) {
          const feedData = await feedRes.json();
          // SESSION 86k: Only show banner if data is NOT flowing
          // If we're connected and data is flowing, ignore backend zombie status
          if (feedData.zombie_killer && feedData.zombie_killer.retry_count > 0) {
            // Only update if we haven't cleared it locally
            setZombieKillerInfo(prev => {
              // If we manually cleared it (prev === null) and connection is good, keep it cleared
              if (prev === null) return null;
              return feedData.zombie_killer;
            });
          } else {
            // Backend says retry_count is 0, always clear
            setZombieKillerInfo(null);
          }
        }
      } catch (err) {
        console.error('Failed to check schedule/feed:', err);
      }
    };
    
    checkDataSource();
    checkScheduleAndFeed();
    const interval = setInterval(() => {
      checkDataSource();
      checkScheduleAndFeed();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // SESSION 71: FORCE-CLEAR zombie banner when connection is established
  // Live data is the ultimate source of truth - if data flows, banner must die
  useEffect(() => {
    if (connectionStatus.isConnected && zombieKillerInfo?.retry_count > 0) {
      console.log('✅ [ZOMBIE KILLER] Connection established - clearing banner');
      setZombieKillerInfo(null);
    }
  }, [connectionStatus.isConnected, zombieKillerInfo]);

  // SESSION 71: AGGRESSIVE CLEAR - Also clear banner when live data arrives
  // This handles edge cases where connection status might not update fast enough
  const lastCvdRef = React.useRef(0);
  useEffect(() => {
    const cvd = sessionStats?.cumulative_delta || 0;
    // If CVD changed significantly (data is flowing), kill the banner
    if (cvd !== 0 && cvd !== lastCvdRef.current && zombieKillerInfo?.retry_count > 0) {
      console.log('✅ [ZOMBIE KILLER] Live data detected (CVD changed) - clearing banner');
      setZombieKillerInfo(null);
    }
    lastCvdRef.current = cvd;
  }, [sessionStats?.cumulative_delta, zombieKillerInfo]);

  // Fetch candles for selected timeframe
  const fetchTimeframeCandles = useCallback(async () => {
    if (selectedTimeframe === "1s") {
      // Use real-time data for 1s
      setTimeframeCandles(candles);
      setTimeframeDelta(deltaData);
      setTimeframeCvd(cumulativeDeltaData);
      setTimeframeVwap(vwapData);
      return;
    }

    try {
      const res = await fetch(`${API}/api/candles/${selectedTimeframe}?limit=300`);
      const data = await res.json();
      
      if (data.candles) {
        // Format candles for chart
        const formattedCandles = data.candles.map(c => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        }));
        setTimeframeCandles(formattedCandles);

        // Format delta data
        const deltaData = data.candles.map(c => ({
          time: c.time,
          value: Math.abs(c.delta || 0),
          color: (c.delta || 0) >= 0 ? '#22c55e' : '#ef4444',  // Green/Red
        }));
        setTimeframeDelta(deltaData);

        // Format CVD data
        const cvdData = data.candles.map(c => ({
          time: c.time,
          value: c.cumulative_delta || 0,
        }));
        setTimeframeCvd(cvdData);

        // Format VWAP data
        const vwapData = data.candles.map(c => ({
          time: c.time,
          value: c.vwap || 0,
        }));
        setTimeframeVwap(vwapData);
      }
    } catch (err) {
      console.error('Failed to fetch timeframe candles:', err);
    }
  }, [selectedTimeframe, candles, deltaData, cumulativeDeltaData, vwapData]);

  // Fetch candles when timeframe changes or periodically for non-1s timeframes
  useEffect(() => {
    fetchTimeframeCandles();
    
    if (selectedTimeframe !== "1s") {
      // Poll for updates every 2 seconds for larger timeframes
      const interval = setInterval(fetchTimeframeCandles, 2000);
      return () => clearInterval(interval);
    }
  }, [selectedTimeframe, fetchTimeframeCandles]);

  // Get latest HFT candle for signal display
  const latestHftCandle = hftCandles.length > 0 ? hftCandles[hftCandles.length - 1] : null;

  return (
    <div className="App terminal-bg min-h-screen flex flex-col" data-testid="algogreeks-app">
      {/* Critical Data Health Alert Banner - Shows when data loss detected */}
      <DataHealthAlert />
      
      {/* Header with Index Selector */}
      <Header 
        isConnected={isConnected} 
        onReset={resetSession} 
        currentSymbol={activeSymbol}
        onSymbolChange={handleSymbolChange}
        dataMode={dataMode}
        marketStatus={marketStatus}
        isLive={isLive}
        connectionState={connectionState}
        isChangingSymbol={isChangingSymbol}
      />

      {/* Session 145 — App-wide Dhan broker health banner. Visible to BOTH
          admins and regular users so they know why auto-execution is paused
          when the token expires or IP drifts. Non-admins see an info-only
          note (no CTA); admins get a link that deep-navigates to the admin
          broker tab. Also rendered inside AdminDashboard for admins already
          on that screen; the dedup doesn't matter visually because the
          banner is sticky, not absolute-positioned. */}
      <div className="px-4 pt-3">
        <DhanHealthBanner
          onNavigateToBroker={() => {
            // Admin may be viewing the trading dashboard when the token
            // expired — deep link them back to the admin broker tab.
            try {
              window.location.hash = '#broker';
              window.location.pathname = '/admin';
            } catch (_e) { /* no-op */ }
          }}
        />
      </div>

      {/* Disconnection Warning Banner - NOT shown in Replay Mode */}
      {isDisconnected && (
        <div className="bg-red-900/80 border-b border-red-500/50 px-4 py-2 flex items-center justify-center gap-2 text-red-200 text-sm backdrop-blur-sm" data-testid="disconnection-banner">
          <span className="animate-pulse">⚠️</span>
          <span className="font-medium">Live Feed Interrupted</span>
          <span className="text-red-400">| Reconnecting...</span>
        </div>
      )}
      
      {/* Dead Zone Banner - Shows when market is closed and no replay available */}
      {/* SESSION 67c: Hide when showing persisted snapshot banner (one banner rule) */}
      {scheduleInfo?.is_dead_zone && !isDisconnected && connectionStatus.status !== 'replay_waiting' && !gexLevels?.is_persisted && (
        <div className="bg-yellow-900/80 border-b border-yellow-500/50 px-4 py-2 flex items-center justify-center gap-2 text-yellow-200 text-sm backdrop-blur-sm" data-testid="dead-zone-banner">
          <span>💤</span>
          <span className="font-medium">Market Offline</span>
          {scheduleInfo?.next_window && (
            <span className="text-yellow-400">
              | Next {scheduleInfo.next_window.mode === 'replay' ? 'Replay' : 'Live'} Session starts at {scheduleInfo.next_window.time ? new Date(scheduleInfo.next_window.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'TBD'}
            </span>
          )}
        </div>
      )}
      
      {/* Zombie Killer Active Banner - Shows when retrying after session conflict */}
      {/* SESSION 86k: Only show when NOT connected - if connected, data is flowing, no need for banner */}
      {zombieKillerInfo?.retry_count > 0 && !connectionStatus.isConnected && (
        <div className="bg-orange-900/80 border-b border-orange-500/50 px-4 py-2 flex items-center justify-center gap-2 text-orange-200 text-sm backdrop-blur-sm" data-testid="zombie-killer-banner">
          <span className="animate-spin">🔄</span>
          <span className="font-medium">Clearing previous sessions...</span>
          <span className="text-orange-400">| Attempt {zombieKillerInfo.retry_count}/{zombieKillerInfo.max_retries} - Please wait</span>
        </div>
      )}
      
      {/* Replay Mode Waiting Banner - Only show when INITIALLY waiting AND in REPLAY mode, not LIVE */}
      {/* SESSION 67c: Hide when viewing persisted data */}
      {connectionStatus.status === 'replay_waiting' && !zombieKillerInfo?.retry_count && !connectionStatus.wasConnected && !isLive && !gexLevels?.is_persisted && (
        <div className="bg-blue-900/80 border-b border-blue-500/50 px-4 py-2 flex items-center justify-center gap-2 text-blue-200 text-sm backdrop-blur-sm" data-testid="replay-waiting-banner">
          <span className="animate-pulse">🔵</span>
          <span className="font-medium">Connecting to Replay Server</span>
          <span className="text-blue-400">| Please wait...</span>
        </div>
      )}
      
      {/* Live Mode Reconnecting Banner - Show when reconnecting in LIVE mode */}
      {/* SESSION 67c: Hide when viewing persisted data */}
      {connectionStatus.status === 'replay_waiting' && !zombieKillerInfo?.retry_count && !connectionStatus.wasConnected && isLive && !gexLevels?.is_persisted && (
        <div className="bg-green-900/80 border-b border-green-500/50 px-4 py-2 flex items-center justify-center gap-2 text-green-200 text-sm backdrop-blur-sm" data-testid="live-reconnecting-banner">
          <span className="animate-pulse">🟢</span>
          <span className="font-medium">Reconnecting to Live Feed</span>
          <span className="text-green-400">| Please wait...</span>
        </div>
      )}
      
      {/* OFF-MARKET SNAPSHOT Banner - SINGLE UNIFIED BANNER for after-hours view */}
      {/* SESSION 67c: Merged market status + snapshot info into one sleek banner */}
      {/* SESSION 67f: Only show when NOT connected or when market is truly closed */}
      {gexLevels?.is_persisted && !connectionStatus.isMarketOpen && !connectionStatus.isConnected && (
        <div className="bg-amber-900/80 border-b border-amber-500/50 px-4 py-2 flex items-center justify-center gap-3 text-amber-200 text-sm backdrop-blur-sm" data-testid="off-market-snapshot-banner">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            <span className="font-semibold uppercase tracking-wide text-amber-300">Market Offline</span>
          </div>
          <span className="text-amber-500/70">|</span>
          <div className="flex items-center gap-2">
            <span>📦</span>
            <span className="font-medium">Viewing Off-Market Snapshot</span>
          </div>
          <span className="text-amber-500/70">|</span>
          <span className="text-amber-400">
            Last saved: {gexLevels?.persisted_date} at {(() => {
              // SESSION 67c: Fix UTC to IST conversion
              if (!gexLevels?.persisted_time) return 'Unknown';
              try {
                const utcDate = new Date(gexLevels.persisted_time);
                // Add 5:30 hours for IST offset
                const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
                return istDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
              } catch {
                return 'Unknown';
              }
            })()}
          </span>
          {/* VIX in Banner - SESSION 128: Use LKG pattern, hide if no data */}
          {godScore?.vix_value && godScore.vix_value > 0 && (
            <>
              <span className="text-amber-500/70">|</span>
              <span className="text-amber-400">
                VIX: <span className={`font-mono ${godScore.vix_value > 18 ? 'text-red-400' : godScore.vix_value < 13 ? 'text-green-400' : 'text-amber-300'}`}>
                  {godScore.vix_value.toFixed(2)}
                </span>
              </span>
            </>
          )}
        </div>
      )}
      
      {/* SESSION 67f: Banner when market IS open but GEX is using persisted data */}
      {/* Only show this banner if greeks_source is not "truedata_local" or "LIVE_GEX" */}
      {gexLevels?.is_persisted && connectionStatus.isMarketOpen && connectionStatus.isConnected && 
       gexLevels?.greeks_source !== "truedata_local" && gexLevels?.greeks_source !== "LIVE_GEX" && (
        <div className="bg-blue-900/80 border-b border-blue-500/50 px-4 py-2 flex items-center justify-center gap-3 text-blue-200 text-sm backdrop-blur-sm" data-testid="greeks-pending-banner">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
            <span className="font-semibold uppercase tracking-wide text-blue-300">LIVE Feed</span>
          </div>
          <span className="text-blue-500/70">|</span>
          <div className="flex items-center gap-2">
            <span>⏳</span>
            <span className="font-medium">GEX calculations initializing...</span>
          </div>
        </div>
      )}

      {/* =====================================================
          MIDNIGHT PRO COCKPIT LAYOUT
          Single cohesive glass sheet design
          ===================================================== */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* ============================================
            MAIN COCKPIT - Charts + KPIs (Flex Grow)
            ============================================ */}
        <div className="cockpit-main">
          
          {/* TOP ROW: KPI Summary Bar - High-Level Metrics Only */}
          <div className="kpi-bar">
            {/* Cumulative Delta with Volume & Aggression % */}
            <div className="kpi-card flex-[1.8]" data-testid="cvd-card">
              <div className="kpi-label">Cumulative Delta</div>
              {/* SESSION 127: Main CVD Value - now Lee-Ready is primary, Aggressor is secondary */}
              <div className="flex items-center justify-center gap-4">
                {/* Lee-Ready CVD - PRIMARY (Industry Standard) */}
                {(() => {
                  const leeReadyCvd = strategyPulse?.hft_breakdown?.lee_ready_cvd 
                    ?? godScore?.hft_breakdown?.lee_ready_cvd 
                    ?? sessionStats?.cumulative_delta
                    ?? 0;
                  return (
                    <div className="text-center">
                      <div className={`kpi-value ${
                        leeReadyCvd > 0 ? 'text-green-400' : 
                        leeReadyCvd < 0 ? 'text-red-400' : 'text-white'
                      }`}>
                        {leeReadyCvd.toLocaleString()}
                      </div>
                      <div className="text-[8px] text-cyan-500">Lee-Ready</div>
                    </div>
                  );
                })()}
                
                {/* Divider */}
                <div className="text-gray-600 text-lg">|</div>
                
                {/* Aggressor CVD - SECONDARY (for comparison) */}
                {/* SESSION 127: Use sessionStats as final fallback to prevent anchoring to 0 */}
                {(() => {
                  const aggressorCvd = strategyPulse?.hft_breakdown?.aggressor_cvd_display 
                    ?? strategyPulse?.hft_breakdown?.aggressor_cvd
                    ?? godScore?.hft_breakdown?.aggressor_cvd_display
                    ?? godScore?.hft_breakdown?.aggressor_cvd
                    ?? sessionStats?.aggressor_cvd
                    ?? 0;
                  return (
                    <div className="text-center">
                      <div className={`kpi-value text-sm ${
                        aggressorCvd > 0 ? 'text-green-400/70' : 
                        aggressorCvd < 0 ? 'text-red-400/70' : 'text-gray-400'
                      }`}>
                        {aggressorCvd.toLocaleString()}
                      </div>
                      <div className="text-[8px] text-gray-500">Aggressor</div>
                    </div>
                  );
                })()}
              </div>
              {/* Secondary Row: Volume, Buy/Sell Split, Aggression */}
              <div className="flex items-center justify-center gap-2 mt-1 text-xs font-mono flex-wrap">
                {/* Session Volume (lots) */}
                {(() => {
                  const vol = sessionStats?.session_volume || 0;
                  const lotSize = activeSymbol === 'BANKNIFTY' ? 30 : 65;
                  const lots = Math.round(vol / lotSize);
                  const volStr = lots >= 100000 ? `${(lots / 100000).toFixed(1)}L` 
                    : lots >= 1000 ? `${(lots / 1000).toFixed(1)}K` 
                    : lots.toLocaleString();
                  return (
                    <div className="flex items-center gap-1" data-testid="session-volume-lots">
                      <span className="text-gray-500">Vol:</span>
                      <span className="text-gray-300">{volStr}</span>
                      <span className="text-gray-600 text-[9px]">lots</span>
                    </div>
                  );
                })()}
                <span className="text-gray-700">|</span>
                {/* Buy/Sell Split */}
                {(() => {
                  const buyVol = sessionStats?.session_buy_volume || 0;
                  const sellVol = sessionStats?.session_sell_volume || 0;
                  const total = buyVol + sellVol;
                  const sellPct = total > 0 ? (sellVol / total * 100) : 50;
                  const buyPct = total > 0 ? (buyVol / total * 100) : 50;
                  const biasColor = sellPct > 55 ? 'text-red-400' : buyPct > 55 ? 'text-green-400' : 'text-gray-400';
                  const biasLabel = sellPct > 55 ? `S${sellPct.toFixed(0)}%` : buyPct > 55 ? `B${buyPct.toFixed(0)}%` : '50/50';
                  return (
                    <div className="flex items-center gap-1" data-testid="buy-sell-split">
                      <span className="text-gray-500">B/S:</span>
                      <span className={`font-semibold ${biasColor}`}>{biasLabel}</span>
                    </div>
                  );
                })()}
                <span className="text-gray-700">|</span>
                {/* Aggression % */}
                {(() => {
                  const cvd = Math.abs(sessionStats?.cumulative_delta || 0);
                  const vol = sessionStats?.session_volume || 1;
                  const aggression = (cvd / vol) * 100;
                  const aggressionColor = aggression < 5 ? 'text-gray-400' : 
                                          aggression >= 15 ? 'text-yellow-400' : 'text-blue-400';
                  const aggressionLabel = aggression < 5 ? 'Chop' : 
                                          aggression >= 15 ? 'Trend' : 'Mixed';
                  return (
                    <div className="flex items-center gap-1" data-testid="aggression-pct">
                      <span className="text-gray-500">Agg:</span>
                      <span className={`font-semibold ${aggressionColor}`}>
                        {aggression.toFixed(1)}%
                      </span>
                      <span className={`text-[10px] px-1 rounded ${
                        aggression < 5 ? 'bg-gray-700 text-gray-400' :
                        aggression >= 15 ? 'bg-yellow-900/50 text-yellow-400' : 
                        'bg-blue-900/50 text-blue-400'
                      }`}>
                        {aggressionLabel}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
            
            {/* SESSION 112: MARKET PROFILE Card - VAH/dPOC/VAL */}
            <div className="kpi-card flex-[1.5]" data-testid="market-profile-card">
              <div className="kpi-label">Market Profile</div>
              <div className="flex items-center justify-center gap-2">
                {/* VAH */}
                <div className="text-center">
                  <div className="text-[8px] text-purple-400 font-medium">VAH</div>
                  <div className="text-sm font-bold font-mono text-purple-300">
                    {strategyPulse?.session_79?.market_profile?.vah?.toLocaleString() || '--'}
                  </div>
                </div>
                <span className="text-gray-600">|</span>
                {/* dPOC */}
                <div className="text-center">
                  <div className="text-[8px] text-amber-400 font-medium">dPOC</div>
                  <div className={`text-sm font-bold font-mono ${
                    strategyPulse?.session_79?.market_profile?.price_vs_dpoc === 'ABOVE' ? 'text-green-400' :
                    strategyPulse?.session_79?.market_profile?.price_vs_dpoc === 'BELOW' ? 'text-red-400' :
                    'text-amber-300'
                  }`}>
                    {strategyPulse?.session_79?.market_profile?.dpoc?.toLocaleString() || '--'}
                  </div>
                </div>
                <span className="text-gray-600">|</span>
                {/* VAL */}
                <div className="text-center">
                  <div className="text-[8px] text-purple-400 font-medium">VAL</div>
                  <div className="text-sm font-bold font-mono text-purple-300">
                    {strategyPulse?.session_79?.market_profile?.val?.toLocaleString() || '--'}
                  </div>
                </div>
              </div>
              {/* Price vs dPOC indicator */}
              <div className="flex justify-center mt-1">
                <span className={`text-[9px] font-semibold px-2 py-0.5 rounded ${
                  strategyPulse?.session_79?.market_profile?.price_vs_dpoc === 'ABOVE'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : strategyPulse?.session_79?.market_profile?.price_vs_dpoc === 'BELOW'
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                }`}>
                  Price {strategyPulse?.session_79?.market_profile?.price_vs_dpoc || 'AT'} dPOC
                </span>
              </div>
            </div>
            
            {/* SESSION 111: GEX Exposure - Compact layout with Gamma badge + stacked Call/Put */}
            <div className="kpi-card flex-[2]">
              <div className="kpi-label flex items-center justify-center gap-2">
                <span>GEX Exposure</span>
                {/* SESSION 111: Gamma Regime Badge (moved from separate card) */}
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                  gexLevels?.gamma_regime === 'NEGATIVE_GAMMA' 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40' 
                    : 'bg-green-500/20 text-green-400 border border-green-500/40'
                }`}>
                  {gexLevels?.gamma_regime === 'NEGATIVE_GAMMA' ? '🔴 -γ' : '🟢 +γ'}
                </span>
                {/* OFF-MARKET SNAPSHOT Badge - Shows when viewing persisted data */}
                {gexLevels?.is_persisted && (
                  <span 
                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/40 animate-pulse cursor-help"
                    title={`Last snapshot: ${gexLevels?.persisted_date || 'Unknown'} at ${gexLevels?.persisted_time ? new Date(gexLevels.persisted_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'Unknown'}`}
                    data-testid="off-market-badge"
                  >
                    📦 SNAPSHOT
                  </span>
                )}
              </div>
              {/* SESSION 111: Stacked layout - NET on top, Call/Put below */}
              <div className="flex flex-col items-center gap-1">
                {/* NET GEX - Large and prominent */}
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400 text-xs font-semibold">NET:</span>
                  <span className={`font-bold text-xl font-mono ${
                    gexLevels?.net_gex_cr === undefined || gexLevels?.net_gex_cr === null
                      ? 'text-gray-500'
                      : gexLevels.net_gex_cr > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {gexLevels?.net_gex_cr === undefined || gexLevels?.net_gex_cr === null
                      ? '...'
                      : `${gexLevels.net_gex_cr > 0 ? '+' : ''}${gexLevels.net_gex_cr.toFixed(2)}Cr`}
                  </span>
                </div>
                {/* Call/Put - Smaller, below NET */}
                <div className="flex items-center gap-3 text-xs font-mono">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500 text-[10px]">CALL:</span>
                    <span className={`font-semibold ${
                      gexLevels?.total_call_gex_cr === undefined ? 'text-gray-500' : 'text-red-400'
                    }`}>
                      {gexLevels?.total_call_gex_cr === undefined ? '...' : `${gexLevels.total_call_gex_cr.toFixed(1)}Cr`}
                    </span>
                  </div>
                  <span className="text-gray-700">|</span>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500 text-[10px]">PUT:</span>
                    <span className={`font-semibold ${
                      gexLevels?.total_put_gex_cr === undefined ? 'text-gray-500' : 'text-green-400'
                    }`}>
                      {gexLevels?.total_put_gex_cr === undefined ? '...' : `+${gexLevels.total_put_gex_cr.toFixed(1)}Cr`}
                    </span>
                  </div>
                  <span className="text-gray-700">|</span>
                  {/* SESSION 112: GEX Anchor Score - moved from left sidebar */}
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500 text-[10px]">ANCHOR:</span>
                    <span className={`font-semibold ${
                      (strategyPulse?.session_79?.gex_score || 0) > 0 ? 'text-green-400' : 
                      (strategyPulse?.session_79?.gex_score || 0) < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {(strategyPulse?.session_79?.gex_score || 0) > 0 ? '+' : ''}{(strategyPulse?.session_79?.gex_score || 0).toFixed(0)}/25
                    </span>
                  </div>
                  <span className="text-gray-700">|</span>
                  {/* SESSION 112: Flip Zone (Zero Gamma) - moved from right sidebar */}
                  <div className="flex items-center gap-1">
                    <span className="text-purple-400 text-[10px]">FLIP:</span>
                    <span className="font-semibold text-purple-300">
                      {gexLevels?.zero_gamma?.toLocaleString() || '--'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* SIGNAL COMMAND CENTER - Replaces Charts */}
          <div className="chart-container">
            <div 
              className="panel h-full"
              data-testid="signal-command-center-panel"
            >
              <SignalCommandCenter 
                niftyPrice={niftyData.futuresPrice || niftyData.spotPrice || 0}
                bankniftyPrice={bankniftyData.futuresPrice || bankniftyData.spotPrice || 0}
                niftyPulse={niftyData.strategyPulse}
                bankniftyPulse={bankniftyData.strategyPulse}
                niftyTrade={niftyData.strategyPulse?.position}
                bankniftyTrade={bankniftyData.strategyPulse?.position}
              />
            </div>
          </div>
        </div>

        {/* ============================================
            RIGHT SIDEBAR: Key Levels + Analysis
            ============================================ */}
        <div className="right-sidebar">
          
          {/* SESSION 112: KEY LEVELS moved to Left Sidebar as Support/Resistance */}
          {/* SESSION 112: FLIP ZONE moved to GEX section in Top Bar */}
          
          {/* GOD SCORE Details - SESSION 120: Added god-score-panel class for CVD comparison visibility */}
          <div 
            className="panel mb-3 god-score-panel"
            data-testid="god-score-command-center"
          >
            <div className="panel-header">
              <span className="panel-title">🎯 God Score Details</span>
            </div>
            <div className="panel-content">
              {/* SESSION 49: Passive Listening Pattern - gets data directly from global store */}
              <GodScoreCommandCenter symbol={activeSymbol} />
            </div>
          </div>

          {/* GEX Analysis Panel - REMOVED: Info now in Header GEX Exposure */}

          {/* Trident Exit Panel */}
          <div 
            className="panel mb-3"
            data-testid="trident-panel"
          >
            <div className="panel-header">
              <span className="panel-title">🔱 Trident Exit</span>
            </div>
            <div className="panel-content">
              <TridentPanel 
                tridentStatus={tridentStatus}
                tradingStats={tradingStats}
              />
            </div>
          </div>

          {/* SESSION 95: Advanced Signals Panel - Phase 1 High-Accuracy Strategies */}
          <div 
            className="panel mb-3"
            data-testid="advanced-signals-container"
          >
            <AdvancedSignalsPanel 
              symbol={activeSymbol}
            />
          </div>

          {/* Stats Panel */}
          <div 
            className="panel mb-3"
            data-testid="stats-panel"
          >
            <div className="panel-header">
              <span className="panel-title">📈 Session Stats</span>
            </div>
            <div className="panel-content">
              <SessionStats 
                stats={sessionStats} 
                latestCandle={latestCandle}
                ltp={ltp}
                currentSymbol={activeSymbol}
                isLoading={isChangingSymbol}
              />
            </div>
          </div>

          {/* SESSION 111: Combined Tape Log + Alerts in Tabbed Panel */}
          <TabbedActivityPanel tape={tape} alerts={alerts} />
        </div>
      </div>
    </div>
  );
};

// Login Page Wrapper
const LoginPage = () => {
  const { login, isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Get the original destination from location state, or default to "/"
  const from = location.state?.from?.pathname || "/";
  
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }
  
  return <AuthPage onLogin={login} />;
};


function App() {
  return (
    <AuthProvider>
      <MarketProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* SESSION 109: Google OAuth callback route */}
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            {/* SESSION 136: Invite route - validates code and shows sign-in */}
            <Route path="/invite/:code" element={<InvitePage />} />
            {/* SESSION 109: Holding page for inactive/expired users */}
            <Route path="/holding" element={<HoldingPage />} />
            {/* All protected routes use MainLayout with Sidebar */}
            {/* Session 147j+16bo (Ship 2) — Root path redirects by role.
                In FOCUSED mode (default), TrueData feeds are off so the
                trading dashboard at "/" has no live ticks. Send admins
                to /admin and users to /dashboard instead. Set
                REACT_APP_FOCUS_MODE=false to restore the trading view. */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <RoleBasedRoot />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/nifty" 
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TradingDashboard key="nifty" initialSymbol="NIFTY" />
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/banknifty" 
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TradingDashboard key="banknifty" initialSymbol="BANKNIFTY" />
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/analytics" 
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <AnalyticsPage />
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <AdminPage />
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            {/* Session 147j+16bo (Ship 2) — Read-only dashboard for ANY
                authenticated user. Invite-only sign-up gates true access;
                this dashboard hides every POST control via the readOnly
                prop on AlphaScoutDashboard + LODROrdersTab. */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            {/* SESSION 75: TrueData Replay Route */}
            <Route 
              path="/replay" 
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <TimeMachinePage />
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            {/* SESSION 91: Time Machine (Local CSV Backtesting) Route */}
            <Route 
              path="/time-machine" 
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <TimeMachinePage />
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
          </Routes>
          {/* Global Toaster for Signal Notifications */}
          <Toaster 
            position="top-right" 
            richColors 
            expand={true}
            closeButton
          />
        </BrowserRouter>
      </MarketProvider>
    </AuthProvider>
  );
}

// Analytics Page Wrapper (no duplicate header needed - MainLayout handles sidebar)
const AnalyticsPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <div className="border-b border-border bg-surface px-4 py-3">
        <h1 className="text-lg font-bold text-foreground">Analytics & Trade History</h1>
        <p className="text-xs text-muted-foreground">Performance metrics and historical analysis</p>
      </div>
      <main className="p-4">
        <AnalyticsDashboard />
      </main>
    </div>
  );
};

// Session 147j+16bo (Ship 2) — Role-based root landing.
//   admin → /admin  (full configuration panel)
//   user  → /dashboard  (read-only AlphaScout + LODR view)
// Falls back to /dashboard when role is missing so newly-onboarded
// users land on something useful instead of a blank trading page.
const RoleBasedRoot = () => {
  const { isAdmin } = useAuth();
  return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
};

// Admin Page Wrapper
const AdminPage = () => {
  return (
    // 147j+16bs — Flex column so AdminDashboard can grow to fill the
    // viewport minus the header. Previously the outer was
    // `min-h-screen` with no flex, and AdminDashboard's `h-screen`
    // overflowed below the viewport, burying the sidebar's logout
    // button. Now AdminDashboard sits inside a `flex-1 min-h-0`
    // slot and respects the available height exactly.
    <div className="h-screen flex flex-col bg-background overflow-hidden" data-testid="admin-page">
      <div className="border-b border-border bg-surface px-4 py-3 shrink-0">
        <h1 className="text-lg font-bold text-foreground">Admin Dashboard</h1>
        <p className="text-xs text-muted-foreground">System configuration and user management</p>
      </div>
      <main className="flex-1 min-h-0 overflow-hidden">
        <AdminDashboard />
      </main>
    </div>
  );
};

// SESSION 75: Time Machine Page Wrapper
const TimeMachinePage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="time-machine-wrapper">
      <TimeMachine />
    </div>
  );
};

export default App;
